import { type FC } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { LoadingSpinner, Tag } from "widgets";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import GenericEntityGenomeView from "../genome-viewer/GenericEntityGenomeView.js";
import { type IGenomeViewOwnProps } from "../genome-viewer/GenomeView.js";
import { renderNuFeatureAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { getPaddedRange } from "@/app-utils/range.ts";
import { type ISortableTrack, getClassicGenomeBrowserTracks } from "@/redux/modules/genome-browser.ts";
import { type INuFeatureState, loadNuFeature } from "@/redux/modules/nuFeature.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { TRACK_TYPE } from "@/settings/settings.js";

const NuFeatureGenomeView: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { nuFeature, isLoading } = useSelector<IAppState, INuFeatureState>((state) => state.nuFeature, shallowEqual);
  // router
  const { nuFeatureId } = useParams<"nuFeatureId">();

  if (isLoading) {
    return (
      <div className={styleLayout.page}>
        <LoadingSpinner inCenter />
      </div>
    );
  }

  let genomeViewProps: IGenomeViewOwnProps;

  if (nuFeature) {
    const nuFeatureTrack: Partial<ISortableTrack> = {
      name: nuFeature.nuFeatureType.name,
      type: TRACK_TYPE.NUFEATURE,
      subTypeId: nuFeature.nuFeatureType.id,
      color: nuFeature.nuFeatureType.color,
      hidden: true,
    };
    const tracks: ISortableTrack[] = getClassicGenomeBrowserTracks([TRACK_TYPE.SEQUENCE, TRACK_TYPE.GENE]);
    tracks.push(nuFeatureTrack as ISortableTrack);

    genomeViewProps = {
      genomeId: nuFeature.genome.id,
      contigId: nuFeature.region.contig.id,
      range: getPaddedRange(nuFeature.region.range, nuFeature.region.contig.length),
      genomes: [nuFeature.genome],
      defaultTracks: tracks,
      attributesWidgets: [
        <AttributesListWidget
          attributes={renderNuFeatureAttributesWidgets(nuFeature)}
          key={"nuFeature-summary-view-widget-1"}
        />,
      ],
      title: nuFeature.name,
      subtitle: "Genome View",
      headerTags: [
        <Tag
          label={nuFeature?.nuFeatureType.name}
          key={"nuFeature-genome-view-tag-1"}
          intentColor={nuFeature?.nuFeatureType?.color}
        />,
      ],
    };
  }

  return (
    <GenericEntityGenomeView
      genomeViewProps={genomeViewProps}
      loadEntityData={() => {
        if (nuFeatureId) {
          dispatch(loadNuFeature(Number(nuFeatureId)));
        }
      }}
    />
  );
};

export default NuFeatureGenomeView;
